import Experience from "@folio-2026/experience"
import { EVENTS } from "@folio-2026/shared"
import gsap from "gsap"
import { GLTF } from "three/examples/jsm/Addons.js"
import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

export default class DeLorean {
  scene: THREE.Scene
  debugFolder?: FolderApi
  mesh!: THREE.Group
  progress: number = 0
  timeline!: gsap.core.Timeline
  wheelBL!: THREE.Mesh
  wheelBR!: THREE.Mesh
  wheelFL!: THREE.Mesh
  wheelFR!: THREE.Mesh
  doorLeft!: THREE.Mesh

  resources: {
    [key: string]: GLTF
  }

  experience: Experience

  constructor({
    scene,
    debugFolder,
    resources,
  }: {
    scene: THREE.Scene
    debugFolder?: FolderApi
    resources: {
      [key: string]: GLTF
    }
  }) {
    this.experience = Experience.getInstance()

    this.scene = scene
    this.debugFolder = debugFolder

    this.resources = resources

    this.setMesh()
    this.setTimeline()
    this.setDebug()

    this.setEventListeners()
  }

  setEventListeners() {
    this.experience.on(EVENTS.Contact.BeforeEnter, () => {
      this.timeline.restart()
    })
  }

  setMesh() {
    this.mesh = this.resources.blender.scene.getObjectByName(
      "car_dmc_delorean-12",
    ) as THREE.Group
    this.mesh.position.set(1.5, 6, -5)
    this.mesh.rotation.z = THREE.MathUtils.degToRad(-90)

    this.wheelBL = this.mesh.getObjectByName("wheelBL") as THREE.Mesh
    this.wheelBR = this.mesh.getObjectByName("wheelBR") as THREE.Mesh
    this.wheelFL = this.mesh.getObjectByName("wheelFL") as THREE.Mesh
    this.wheelFR = this.mesh.getObjectByName("wheelFR") as THREE.Mesh

    this.doorLeft = this.mesh.getObjectByName("detach_door_L_80") as THREE.Mesh

    this.wheelBL.rotation.y = THREE.MathUtils.degToRad(90)
    this.wheelBR.rotation.y = THREE.MathUtils.degToRad(90)
    this.wheelFL.rotation.x = THREE.MathUtils.degToRad(0)
    this.wheelFL.rotation.y = THREE.MathUtils.degToRad(75)
    this.wheelFR.rotation.x = THREE.MathUtils.degToRad(0)
    this.wheelFR.rotation.y = THREE.MathUtils.degToRad(-75)

    this.scene.add(this.mesh)
  }

  setTimeline() {
    this.timeline = gsap.timeline({
      paused: true,
    })

    this.timeline
      .to(this.mesh.position, {
        x: 3,
        y: 0,
        z: -8,
        duration: 10,
        ease: "power3.inOut",
      })
      .to(
        this.mesh.rotation,
        {
          z: THREE.MathUtils.degToRad(-170),
          duration: 4,
          ease: "power3.inOut",
        },
        0,
      )
      .to(
        this.wheelBL.rotation,
        {
          y: 0,
          duration: 10,
          ease: "power3.inOut",
        },
        0,
      )
      .to(
        this.wheelFL.rotation,
        {
          y: 0,
          duration: 10,
          ease: "power3.inOut",
        },
        0,
      )
      .to(
        this.wheelFR.rotation,
        {
          y: 0,
          duration: 10,
          ease: "power3.inOut",
        },
        0,
      )
      .to(this.doorLeft.rotation, {
        z: THREE.MathUtils.degToRad(15),
        delay: 3,
        duration: 5,
        ease: "power3.inOut",
      })
  }

  setDebug() {
    if (!this.debugFolder) {
      return
    }

    const folder = this.debugFolder.addFolder({
      title: "🚗 DeLorean",
      expanded: false,
    })

    const progressControl = {
      value: this.progress,
    }

    folder
      .addBinding(progressControl, "value", {
        label: "Progress",
        min: 0,
        max: 1,
        step: 0.001,
      })
      .on("change", (ev) => {
        this.progress = ev.value
        this.timeline.progress(ev.value)
      })

    const position = {
      x: this.mesh.position.x,
      y: this.mesh.position.y,
      z: this.mesh.position.z,
    }

    folder
      .addBinding(position, "x", {
        label: "Position X",
        min: -10,
        max: 10,
        step: 0.1,
      })
      .on("change", (ev) => {
        this.mesh.position.x = ev.value
      })

    folder
      .addBinding(position, "y", {
        label: "Position Y",
        min: -10,
        max: 10,
        step: 0.1,
      })
      .on("change", (ev) => {
        this.mesh.position.y = ev.value
      })

    folder
      .addBinding(position, "z", {
        label: "Position Z",
        min: -10,
        max: 10,
        step: 0.1,
      })
      .on("change", (ev) => {
        this.mesh.position.z = ev.value
      })

    // Wheels rotation controls
    const wheelsFolder = folder.addFolder({
      title: "Wheels",
      expanded: false,
    })

    // Wheel BL (Back Left)
    const wheelBLRotation = {
      x: THREE.MathUtils.radToDeg(this.wheelBL.rotation.x),
      y: THREE.MathUtils.radToDeg(this.wheelBL.rotation.y),
      z: THREE.MathUtils.radToDeg(this.wheelBL.rotation.z),
    }

    wheelsFolder
      .addBinding(wheelBLRotation, "x", {
        label: "BL Rotation X",
        min: -180,
        max: 180,
        step: 1,
      })
      .on("change", (ev) => {
        this.wheelBL.rotation.x = THREE.MathUtils.degToRad(ev.value)
      })

    wheelsFolder
      .addBinding(wheelBLRotation, "y", {
        label: "BL Rotation Y",
        min: -180,
        max: 180,
        step: 1,
      })
      .on("change", (ev) => {
        this.wheelBL.rotation.y = THREE.MathUtils.degToRad(ev.value)
      })

    wheelsFolder
      .addBinding(wheelBLRotation, "z", {
        label: "BL Rotation Z",
        min: -180,
        max: 180,
        step: 1,
      })
      .on("change", (ev) => {
        this.wheelBL.rotation.z = THREE.MathUtils.degToRad(ev.value)
      })

    // Wheel BR (Back Right)
    const wheelBRRotation = {
      x: THREE.MathUtils.radToDeg(this.wheelBR.rotation.x),
      y: THREE.MathUtils.radToDeg(this.wheelBR.rotation.y),
      z: THREE.MathUtils.radToDeg(this.wheelBR.rotation.z),
    }

    wheelsFolder
      .addBinding(wheelBRRotation, "x", {
        label: "BR Rotation X",
        min: -180,
        max: 180,
        step: 1,
      })
      .on("change", (ev) => {
        this.wheelBR.rotation.x = THREE.MathUtils.degToRad(ev.value)
      })

    wheelsFolder
      .addBinding(wheelBRRotation, "y", {
        label: "BR Rotation Y",
        min: -180,
        max: 180,
        step: 1,
      })
      .on("change", (ev) => {
        this.wheelBR.rotation.y = THREE.MathUtils.degToRad(ev.value)
      })

    wheelsFolder
      .addBinding(wheelBRRotation, "z", {
        label: "BR Rotation Z",
        min: -180,
        max: 180,
        step: 1,
      })
      .on("change", (ev) => {
        this.wheelBR.rotation.z = THREE.MathUtils.degToRad(ev.value)
      })

    // Wheel FL (Front Left)
    const wheelFLRotation = {
      x: THREE.MathUtils.radToDeg(this.wheelFL.rotation.x),
      y: THREE.MathUtils.radToDeg(this.wheelFL.rotation.y),
      z: THREE.MathUtils.radToDeg(this.wheelFL.rotation.z),
    }

    wheelsFolder
      .addBinding(wheelFLRotation, "x", {
        label: "FL Rotation X",
        min: -180,
        max: 180,
        step: 1,
      })
      .on("change", (ev) => {
        this.wheelFL.rotation.x = THREE.MathUtils.degToRad(ev.value)
      })

    wheelsFolder
      .addBinding(wheelFLRotation, "y", {
        label: "FL Rotation Y",
        min: -180,
        max: 180,
        step: 1,
      })
      .on("change", (ev) => {
        this.wheelFL.rotation.y = THREE.MathUtils.degToRad(ev.value)
      })

    wheelsFolder
      .addBinding(wheelFLRotation, "z", {
        label: "FL Rotation Z",
        min: -180,
        max: 180,
        step: 1,
      })
      .on("change", (ev) => {
        this.wheelFL.rotation.z = THREE.MathUtils.degToRad(ev.value)
      })

    // Wheel FR (Front Right)
    const wheelFRRotation = {
      x: THREE.MathUtils.radToDeg(this.wheelFR.rotation.x),
      y: THREE.MathUtils.radToDeg(this.wheelFR.rotation.y),
      z: THREE.MathUtils.radToDeg(this.wheelFR.rotation.z),
    }

    wheelsFolder
      .addBinding(wheelFRRotation, "x", {
        label: "FR Rotation X",
        min: -180,
        max: 180,
        step: 1,
      })
      .on("change", (ev) => {
        this.wheelFR.rotation.x = THREE.MathUtils.degToRad(ev.value)
      })

    wheelsFolder
      .addBinding(wheelFRRotation, "y", {
        label: "FR Rotation Y",
        min: -180,
        max: 180,
        step: 1,
      })
      .on("change", (ev) => {
        this.wheelFR.rotation.y = THREE.MathUtils.degToRad(ev.value)
      })

    wheelsFolder
      .addBinding(wheelFRRotation, "z", {
        label: "FR Rotation Z",
        min: -180,
        max: 180,
        step: 1,
      })
      .on("change", (ev) => {
        this.wheelFR.rotation.z = THREE.MathUtils.degToRad(ev.value)
      })
  }
}
